import { PlayButton } from "@southfields-digital/mxmz-react-ui";
import classNames from "classnames";
import { useEffect, useMemo } from "react";

import type { Clock } from "src/types/matchControl/clock";
import type { Control } from "src/types/matchControl/control";

import { useTimer } from "../hooks";
import styles from "../MatchControl.module.scss";

import ClockStart from "./ClockStart";
import TimeAdjustButtons from "./TimeAdjustButtons";
import TimeIn from "./TimeIn";

const MAIN_CLOCK_ID = "clock";

type ClockRowProps = {
	mainClock: Clock;
	matchControlGraphics: Control[];
	isCompact?: boolean;
	handleStartTime: (clock: Clock) => void;
	handleStopTime: (clock: Clock) => void;
	handleDispatchControl: (control?: Control) => void;
	handleChangeTime: (clock: Clock, time: number) => void;
};

const ClockRow = ({
	mainClock,
	matchControlGraphics,
	isCompact = false,
	handleStartTime,
	handleStopTime,
	handleDispatchControl,
	handleChangeTime,
}: ClockRowProps) => {
	const { time: mainClockTime, setTimer } = useTimer(
		10,
		mainClock.clockTime,
		mainClock.clockCountDirection,
	);

	const isMatchControlMainClockLive = useMemo(
		() =>
			!!matchControlGraphics?.find(
				(control) => control.controlId === MAIN_CLOCK_ID && control.live,
			),
		[matchControlGraphics],
	);

	// Sync the local timer with server-time offset whenever the clock state changes
	useEffect(() => {
		if (!mainClock) return;

		const serverTimeOffset = mainClock.serverTimeUpdate
			? new Date(mainClock.serverTimeUpdate).getTime() - Date.now()
			: 0;

		const startedAt = mainClock.clockTimeUpdatedAt
			? new Date(mainClock.clockTimeUpdatedAt).getTime() - serverTimeOffset
			: null;

		setTimer(mainClock.clockTime || 0, startedAt ? new Date(startedAt) : null);
	}, [mainClock, mainClock?.clockTimeUpdatedAt, mainClock?.clockTime]);

	return (
		<>
			<div className={styles.ExtraInWrapper}>
				<ClockStart
					isRunning={!!mainClock.clockTimeUpdatedAt}
					handleStartTimer={() => handleStartTime(mainClock)}
					handleStopTimer={() => handleStopTime(mainClock)}
					minimize={isCompact}
				/>

				<PlayButton
					isRunning={isMatchControlMainClockLive}
					onClick={() =>
						handleDispatchControl(matchControlGraphics?.find((c) => c.controlId === MAIN_CLOCK_ID))
					}
					className={classNames("w-full place-content-center", isCompact ? "!py-1.5" : "!py-3")}
				>
					CLOCK
				</PlayButton>
			</div>

			<div className="flex flex-col gap-4">
				<TimeIn
					time={mainClockTime ?? 0}
					precision="minutes"
					handleChangeTime={(time) => handleChangeTime(mainClock, mainClock.clockTime + time)}
					isCompact={isCompact}
				/>

				<TimeAdjustButtons
					onAdd={(amount) => handleChangeTime(mainClock, mainClock.clockTime + amount * 1000)}
					onSubtract={(amount) => handleChangeTime(mainClock, mainClock.clockTime - amount * 1000)}
					isCompact={isCompact}
				/>
			</div>
		</>
	);
};

export default ClockRow;
